import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Gift, Search, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { fmtDate } from '@/lib/utils';

const STATUS_STYLE = {
  active: 'text-green-400 bg-green-400/10',
  redeemed: 'text-white/40 bg-white/5',
  expired: 'text-red-400 bg-red-400/10',
  cancelled: 'text-red-400 bg-red-400/10',
};

// Accrual summary strip — 4 tiles covering the full liability picture for the
// currently-filtered cohort. Outstanding Balance stays in primary red so it
// reads as the live liability figure.
function AccrualStrip({ cards }) {
  const totalLoaded = cards.reduce((s, c) => s + Number(c.initial_amount || 0), 0);
  const totalRedeemed = cards.reduce((s, c) => s + (Number(c.initial_amount || 0) - Number(c.balance || 0)), 0);
  const totalOutstanding = cards.reduce((s, c) => s + Number(c.balance || 0), 0);

  const tiles = [
    { label: 'CARDS SOLD', value: cards.length, accent: false },
    { label: 'TOTAL LOADED', value: `£${totalLoaded.toFixed(2)}`, accent: false },
    { label: 'TOTAL REDEEMED', value: `£${totalRedeemed.toFixed(2)}`, accent: false },
    { label: 'OUTSTANDING BALANCE', value: `£${totalOutstanding.toFixed(2)}`, accent: true },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {tiles.map((t) => (
        <div
          key={t.label}
          className={t.accent ? 'border border-primary/20 bg-primary/5 px-4 py-3.5' : 'border border-white/5 bg-[#111115] px-4 py-3.5'}
        >
          <p className={`text-[9px] font-bold tracking-widest ${t.accent ? 'text-primary' : 'text-white/30'}`}>{t.label}</p>
          <p className={`font-black text-xl mt-1 ${t.accent ? 'text-primary' : 'text-white'}`}>{t.value}</p>
        </div>
      ))}
    </div>
  );
}

// Date-range picker with quick shortcuts. Filters by purchase/issue date
// (GiftCard.created_date). Empty range = all time.
function DateRangePicker({ from, to, onApply }) {
  const [draftFrom, setDraftFrom] = useState(from || '');
  const [draftTo, setDraftTo] = useState(to || '');

  const apply = (f, t) => {
    setDraftFrom(f);
    setDraftTo(t);
    onApply(f, t);
  };

  const quickShortcut = (kind) => {
    const now = new Date();
    if (kind === 'all') { apply('', ''); return; }
    const y = now.getFullYear();
    const m = now.getMonth();
    const pad = (n) => String(n).padStart(2, '0');
    if (kind === 'this') {
      apply(`${y}-${pad(m + 1)}-01`, `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`);
    } else if (kind === 'last') {
      const lm = m === 0 ? 11 : m - 1;
      const ly = m === 0 ? y - 1 : y;
      apply(`${ly}-${pad(lm + 1)}-01`, `${ly}-${pad(lm + 1)}-${pad(new Date(ly, lm + 1, 0).getDate())}`);
    }
  };

  const isActive = (kind) => {
    if (kind === 'all') return !from && !to;
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const pad = (n) => String(n).padStart(2, '0');
    if (kind === 'this') {
      return from === `${y}-${pad(m + 1)}-01`;
    }
    if (kind === 'last') {
      const lm = m === 0 ? 11 : m - 1;
      const ly = m === 0 ? y - 1 : y;
      return from === `${ly}-${pad(lm + 1)}-01`;
    }
    return false;
  };

  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[120px]">
          <label className="block text-white/30 text-[9px] font-bold tracking-widest mb-1">FROM</label>
          <Input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} className="bg-[#111115] border-white/10 text-white h-9 focus:border-primary/60" />
        </div>
        <div className="flex-1 min-w-[120px]">
          <label className="block text-white/30 text-[9px] font-bold tracking-widest mb-1">TO</label>
          <Input type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} className="bg-[#111115] border-white/10 text-white h-9 focus:border-primary/60" />
        </div>
        <button
          onClick={() => apply(draftFrom, draftTo)}
          className="bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-wider px-4 h-9 transition-colors flex items-center gap-1.5"
        >
          <Calendar className="w-3 h-3" /> APPLY
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {[
          { key: 'this', label: 'This month' },
          { key: 'last', label: 'Last month' },
          { key: 'all', label: 'All time' },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => quickShortcut(s.key)}
            className={`text-[10px] font-bold tracking-wider px-3 py-1.5 border transition-colors ${
              isActive(s.key)
                ? 'border-primary/50 bg-primary/10 text-primary'
                : 'border-white/10 text-white/50 hover:text-white hover:border-white/30'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Gift card purchase receipts tab. Gift card purchases are receipt-only (no VAT
// invoice, no Sage export) until redeemed — at which point an Order + invoice is
// created for whatever was bought. This tab lets staff see gift card revenue
// separately from taxable sales, and report accrual balances for any period.
export default function GiftCardReceiptsTab() {
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data: cards = [], isLoading } = useQuery({
    queryKey: ['admin-gift-card-receipts'],
    queryFn: () => base44.entities.GiftCard.list('-created_date', 500),
  });

  // Filter by purchase date range first, then search term within that set.
  const rangeFiltered = useMemo(() => {
    if (!from && !to) return cards;
    const fromTime = from ? new Date(from + 'T00:00:00').getTime() : 0;
    const toTime = to ? new Date(to + 'T23:59:59').getTime() : Infinity;
    return cards.filter((c) => {
      if (!c.created_date) return false;
      const t = new Date(c.created_date).getTime();
      return t >= fromTime && t <= toTime;
    });
  }, [cards, from, to]);

  const q = search.toLowerCase();
  const filtered = rangeFiltered.filter((c) => {
    if (!q) return true;
    return [
      c.code, c.purchaser_name, c.purchaser_email,
      c.recipient_name, c.recipient_email, c.square_payment_id,
    ].filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  return (
    <div>
      <AccrualStrip cards={rangeFiltered} />

      <DateRangePicker from={from} to={to} onApply={(f, t) => { setFrom(f); setTo(t); }} />

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search code, purchaser, recipient, payment ID..."
          className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-white/5 bg-[#111115]">
          <Gift className="w-10 h-10 text-white/10 mx-auto mb-4" />
          <p className="text-white/30 text-sm">No gift card receipts found</p>
        </div>
      ) : (
        <>
          {/* Desktop table — unchanged multi-column row */}
          <div className="hidden sm:block border border-white/5 overflow-hidden">
            <div className="flex items-center gap-4 px-5 py-3 bg-[#111115] border-b border-white/5">
              <p className="text-white/30 text-[10px] font-bold tracking-widest w-28 shrink-0">CODE</p>
              <p className="text-white/30 text-[10px] font-bold tracking-widest w-20 shrink-0 hidden md:block">DATE</p>
              <p className="text-white/30 text-[10px] font-bold tracking-widest flex-1">PURCHASER</p>
              <p className="text-white/30 text-[10px] font-bold tracking-widest flex-1 hidden sm:block">RECIPIENT</p>
              <p className="text-white/30 text-[10px] font-bold tracking-widest w-24 text-right shrink-0">LOADED</p>
              <p className="text-white/30 text-[10px] font-bold tracking-widest w-24 text-right shrink-0">BALANCE</p>
              <p className="text-white/30 text-[10px] font-bold tracking-widest w-24 text-center shrink-0">STATUS</p>
            </div>
            {filtered.map((c) => {
              const status = c.status || 'active';
              return (
                <div key={c.id} className="flex items-center gap-4 px-5 py-3 border-b border-white/5 hover:bg-white/5 transition-colors">
                  <p className="text-white font-mono font-bold text-xs w-28 shrink-0 truncate">{c.code || '—'}</p>
                  <p className="text-white/40 text-xs w-20 shrink-0 hidden md:block">{fmtDate(c.created_date) || '—'}</p>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{c.purchaser_name || '—'}</p>
                    <p className="text-white/40 text-xs truncate">{c.purchaser_email || '—'}</p>
                  </div>
                  <div className="flex-1 min-w-0 hidden sm:block">
                    <p className="text-white/70 text-sm truncate">{c.recipient_name || '—'}</p>
                    <p className="text-white/40 text-xs truncate">{c.recipient_email || '—'}</p>
                  </div>
                  <p className="text-white font-bold text-sm w-24 text-right shrink-0">£{Number(c.initial_amount || 0).toFixed(2)}</p>
                  <p className="text-primary font-bold text-sm w-24 text-right shrink-0">£{Number(c.balance || 0).toFixed(2)}</p>
                  <span className={`text-[10px] font-bold tracking-wider px-2 py-1 w-24 text-center shrink-0 ${STATUS_STYLE[status] || STATUS_STYLE.active}`}>
                    {status.toUpperCase()}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Mobile cards — stacked so all values fit at 384px */}
          <div className="sm:hidden space-y-2">
            {filtered.map((c) => {
              const status = c.status || 'active';
              return (
                <div key={c.id} className="border border-white/5 bg-[#111115] p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-white font-mono font-bold text-xs truncate flex-1">{c.code || '—'}</p>
                    <span className={`text-[9px] font-bold tracking-wider px-2 py-0.5 shrink-0 ${STATUS_STYLE[status] || STATUS_STYLE.active}`}>
                      {status.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-white text-sm font-medium truncate mb-0.5">{c.purchaser_name || '—'}</p>
                  <p className="text-white/40 text-xs truncate mb-2">{c.purchaser_email || '—'}</p>
                  <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/5">
                    <div>
                      <p className="text-white/30 text-[9px] font-bold tracking-widest">LOADED</p>
                      <p className="text-white font-bold text-sm">£{Number(c.initial_amount || 0).toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-primary text-[9px] font-bold tracking-widest">BALANCE</p>
                      <p className="text-primary font-bold text-sm">£{Number(c.balance || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <p className="text-white/20 text-xs mt-4 leading-relaxed">
        Gift card purchases are receipts only — no VAT invoice is issued and they are excluded from Sage exports until redeemed. When a gift card is used to buy goods or services, an Order + VAT invoice is created at that point and added to the Sales list.
      </p>
    </div>
  );
}